import React from "react";
import {
  Box,
  Chip,
  CircularProgress,
  LinearProgress,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { CeleryTask } from "../../../api/celery";
import { ColorsEnum } from "../../../common/theme/colors";

const STATE_COLOURS: Record<string, string> = {
  PENDING: ColorsEnum.sunshine,
  RECEIVED: ColorsEnum.machoBlue,
  STARTED: ColorsEnum.lighterMachoBlue,
  SUCCESS: ColorsEnum.green,
  FAILURE: ColorsEnum.error,
  RETRY: ColorsEnum.lavender,
  REVOKED: ColorsEnum.grey,
};

const fmtRuntime = (task: CeleryTask): string => {
  if (task.runtime != null) return `${task.runtime.toFixed(1)}s`;
  if (task.started) {
    const elapsed = (Date.now() / 1000) - task.started;
    return `${elapsed.toFixed(0)}s`;
  }
  return "—";
};

interface EmbedTasksPanelProps {
  tasks: CeleryTask[];
  loading: boolean;
  expanded: boolean;
  onToggle: () => void;
}

const EmbedTasksPanel: React.FC<EmbedTasksPanelProps> = ({
  tasks,
  loading,
  onToggle,
}) => {
  const activeCount = tasks.filter((t) =>
    ["STARTED", "PENDING", "RECEIVED", "RETRY"].includes(
      (t.state || "").toUpperCase()
    )
  ).length;

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        height: 130,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.25,
          cursor: "pointer",
          "&:hover": { bgcolor: "action.hover" },
        }}
        onClick={onToggle}
      >
        {loading ? (
          <CircularProgress size={14} sx={{ flexShrink: 0 }} />
        ) : (
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: activeCount > 0 ? ColorsEnum.lighterMachoBlue : ColorsEnum.grey,
              flexShrink: 0,
              animation: activeCount > 0 ? "pulse 1.4s ease-in-out infinite" : "none",
              "@keyframes pulse": {
                "0%, 100%": { opacity: 1 },
                "50%": { opacity: 0.3 },
              },
            }}
          />
        )}

        <Typography variant="body2" fontWeight={600} sx={{ flexGrow: 1 }}>
          Embedding Tasks
          {!loading && (
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ ml: 1 }}
            >
              {activeCount > 0
                ? `${activeCount} running`
                : `${tasks.length} recent`}
            </Typography>
          )}
        </Typography>

        {activeCount > 0 && (
          <Chip
            label={activeCount}
            size="small"
            sx={{
              bgcolor: ColorsEnum.lighterMachoBlue,
              color: ColorsEnum.white,
              fontWeight: 700,
              fontSize: "0.7rem",
              height: 20,
            }}
          />
        )}
      </Box>

      {/* Active progress bar when tasks are running */}
      {activeCount > 0 && (
        <LinearProgress
          variant="indeterminate"
          sx={{ height: 2, bgcolor: "transparent" }}
        />
      )}

      {/* Task list */}
        <Box
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            height: 200,
            overflowY: "auto",
          }}
        >
          {tasks.map((task) => {
            const state = (task.state || "UNKNOWN").toUpperCase();
            const isActive = ["STARTED", "PENDING", "RECEIVED", "RETRY"].includes(state);
            return (
              <Box
                key={task.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2,
                  py: 1,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  "&:last-child": { borderBottom: "none" },
                  opacity: isActive ? 1 : 0.65,
                }}
              >
                {/* State chip */}
                <Chip
                  label={state}
                  size="small"
                  sx={{
                    bgcolor: STATE_COLOURS[state] || ColorsEnum.grey,
                    color: ColorsEnum.white,
                    fontWeight: 600,
                    fontSize: "0.65rem",
                    height: 20,
                    minWidth: 64,
                  }}
                />

                {/* Task name */}
                <Typography
                  variant="caption"
                  sx={{
                    flexGrow: 1,
                    fontFamily: "monospace",
                    fontSize: "0.75rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={task.name || task.id}
                >
                  {task.name || "(unnamed)"}
                </Typography>

                {/* Runtime */}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ flexShrink: 0, fontSize: "0.75rem", minWidth: 40, textAlign: "right" }}
                >
                  {fmtRuntime(task)}
                </Typography>

                {/* ID + link to full detail */}
                <Tooltip title={task.id}>
                  <Link
                    href={`/celery/${task.id}`}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      color: "text.secondary",
                      flexShrink: 0,
                    }}
                  >
                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                  </Link>
                </Tooltip>
              </Box>
            );
          })}

          {!loading && tasks.length === 0 && (
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                No embedding tasks found.
              </Typography>
            </Box>
          )}
        </Box>
    </Box>
  );
};

export default EmbedTasksPanel;
